/**
 * The shared ledger, cloud side: what happens when two devices keep one
 * company's bookkeeping in one cloud folder.
 *
 * The domain decides *when* a write has to be serialised through the remote
 * copy (`src/domain/sync/shared-ledger.ts`: pull, write, publish-before-
 * commit, retry when the remote moved). This module supplies the four
 * operations that make it real, on the RemoteStore port and the backup
 * module, and installs them at start-up:
 *
 *  - `isShared`: the active store checks `ifVersion` itself
 *    (`capabilities.conditionalPut` — Koofr) and the company has its folder
 *    tree there. Drive keeps the single-writer behaviour it always had.
 *  - `pull`: `pullSnapshot` — merge whatever another device pushed. Refuses
 *    to start when the browser is offline: a shared bookkeeping cannot number
 *    a verifikation without asking the copy every device agrees on.
 *  - `publish`: export the company from inside the booking's transaction and
 *    upload it under `Dexie.waitFor`, which keeps that transaction alive while
 *    the network call runs and executes the upload outside of it. The upload
 *    itself only writes `driveState` and `backupLog`.
 *  - `lock`: the Web Locks API, so two tabs never interleave pull and push;
 *    an in-process queue where the API is missing (tests).
 *
 * It also runs the watcher that notices another device's changes while the
 * app is open, and publishes what a merge did so the shell can say so.
 */
import Dexie from 'dexie';
import { remoteStore, type RemoteStore } from '@/cloud/store';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { registerDevice } from '@/domain/devices/service';
import { LedgerConflictError, LedgerOfflineError, configureSharedLedger } from '@/domain/sync/shared-ledger';
import type { IsoDateTime, Uuid } from '@/domain/types';
import { defaultBackupDeps, exportCompany, publishSnapshot, pullSnapshot, type BackupDeps, type PullResult, type SnapshotOrigin } from './backup';
import type { MergeSummary } from './merge';
import { getDriveState } from './tree';

/** How long a booking's transaction may wait for the upload before it is given up. */
export const PUBLISH_TIMEOUT_MS = 120_000;
/** How often the watcher asks the remote whether another device wrote, while the tab is visible. */
export const LEDGER_WATCH_INTERVAL_MS = 60_000;

export interface LedgerDeps {
  store: () => RemoteStore | null;
  backup: BackupDeps;
  /** False when the browser knows it is offline; `navigator.onLine`. */
  online: () => boolean;
  now: () => Date;
  /** Mutual exclusion by name; the Web Locks API when present. */
  lock: <T>(name: string, fn: () => Promise<T>) => Promise<T>;
}

// ---- Locks ---------------------------------------------------------------------

const localLocks = new Map<string, Promise<unknown>>();

/** A per-name queue for environments without `navigator.locks`. */
export async function inProcessLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const previous = localLocks.get(name) ?? Promise.resolve();
  let release: () => void = () => {};
  const gate = new Promise<void>((resolve) => {
    release = resolve;
  });
  const mine = previous.then(() => gate, () => gate);
  localLocks.set(name, mine);
  await previous.catch(() => undefined);
  try {
    return await fn();
  } finally {
    release();
    if (localLocks.get(name) === mine) localLocks.delete(name);
  }
}

async function webLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : (navigator as Navigator & { locks?: LockManager }).locks;
  if (locks && typeof locks.request === 'function') return (await locks.request(name, () => fn())) as T;
  return inProcessLock(name, fn);
}

/** One lock per company and database, so a test's two "devices" do not queue behind each other. */
export function ledgerLockName(db: BookkeepingDB, companyId: Uuid): string {
  return `egenbalans-ledger:${db.name}:${companyId}`;
}

export const defaultLedgerDeps: LedgerDeps = {
  store: remoteStore,
  backup: defaultBackupDeps,
  online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
  now: () => new Date(),
  lock: webLock,
};

// ---- Events --------------------------------------------------------------------

export type LedgerEvent =
  | { type: 'merged'; companyId: Uuid; at: IsoDateTime; summary: MergeSummary; writtenBy?: SnapshotOrigin }
  | { type: 'conflict'; companyId: Uuid; at: IsoDateTime; message: string };

const listeners = new Set<(event: LedgerEvent) => void>();

/** Subscribe to merges and collisions, whichever path triggered them (a booking, the scheduler, the watcher). */
export function onLedgerEvent(listener: (event: LedgerEvent) => void): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

function emit(event: LedgerEvent): void {
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch {
      // A listener is UI; it must never break the sync.
    }
  }
}

// ---- The operations --------------------------------------------------------------

/** True when this company's writes go through the remote copy. */
export async function isSharedLedger(companyId: Uuid, db: BookkeepingDB = defaultDb, deps: LedgerDeps = defaultLedgerDeps): Promise<boolean> {
  const store = deps.store();
  if (!store?.capabilities.conditionalPut) return false;
  const state = await getDriveState(companyId, db);
  return !!state.folders;
}

/**
 * Merge another device's changes, and tell the shell what arrived. Runs
 * without the lock: callers hold it (the booking wrapper, the scheduler) or
 * use `pullLedgerNow`.
 */
export async function pullLedger(companyId: Uuid, db: BookkeepingDB = defaultDb, deps: LedgerDeps = defaultLedgerDeps): Promise<PullResult> {
  const at = deps.now().toISOString();
  try {
    const result = await pullSnapshot(companyId, db, deps.backup);
    if (result.changed && result.summary) emit({ type: 'merged', companyId, at, summary: result.summary, ...(result.writtenBy ? { writtenBy: result.writtenBy } : {}) });
    return result;
  } catch (e) {
    if (e instanceof LedgerConflictError) emit({ type: 'conflict', companyId, at, message: e.message });
    throw e;
  }
}

/** Take the company's lock and run `fn`. */
export function withLedgerLock<T>(companyId: Uuid, db: BookkeepingDB, fn: () => Promise<T>, deps: LedgerDeps = defaultLedgerDeps): Promise<T> {
  return deps.lock(ledgerLockName(db, companyId), fn);
}

/** "Hämta ändringar": a pull under the lock, for the watcher and the settings page. */
export function pullLedgerNow(companyId: Uuid, db: BookkeepingDB = defaultDb, deps: LedgerDeps = defaultLedgerDeps): Promise<PullResult> {
  return withLedgerLock(companyId, db, () => pullLedger(companyId, db, deps), deps);
}

/**
 * The publish step of a booking: the company as it looks inside the calling
 * transaction, uploaded on the condition that the remote still is what the
 * pull saw. A `LedgerMovedError` from the upload rejects this promise, which
 * rejects the transaction, which rolls the booking back.
 */
export async function publishFromTransaction(companyId: Uuid, db: BookkeepingDB = defaultDb, deps: LedgerDeps = defaultLedgerDeps): Promise<void> {
  // Read inside the transaction: this is the only way the snapshot can hold
  // the rows the flow has just written but not yet committed.
  const snapshot = await exportCompany(companyId, db);
  const at = deps.now().toISOString();
  await Dexie.waitFor(() => publishSnapshot(companyId, snapshot, db, deps.backup, { kind: 'auto', at, extras: false }), PUBLISH_TIMEOUT_MS);
}

/**
 * Register the four operations with the domain. Called once from
 * `wiring.ts`; tests call it with their own deps and a fake store.
 */
export function installSharedLedger(deps: LedgerDeps = defaultLedgerDeps): void {
  configureSharedLedger({
    isShared: (companyId, db) => isSharedLedger(companyId, db, deps),
    pull: async (companyId, db) => {
      if (!deps.online()) throw new LedgerOfflineError(deps.store()?.label ?? 'lagringen');
      /*
       * Name this device in the copy the flow is about to publish. Outside
       * the flow's transaction, because it writes `devices` and reads
       * `settings`. Best effort: a label must never fail a booking.
       */
      try {
        await registerDevice(companyId, db);
      } catch {
        /* no identity yet; the snapshot just omits it */
      }
      await pullLedger(companyId, db, deps);
    },
    publish: (companyId, db) => publishFromTransaction(companyId, db, deps),
    lock: (companyId, db, fn) => withLedgerLock(companyId, db, fn, deps),
  });
}

// ---- The watcher -----------------------------------------------------------------

export interface LedgerWatchOptions {
  /** The company to keep current; null pauses the loop (no company, no backend). */
  companyId: () => Uuid | null | Promise<Uuid | null>;
  intervalMs?: number;
  db?: BookkeepingDB;
  deps?: LedgerDeps;
  /** Offline, a token that expired, a collision already published as an event: reported, never thrown. */
  onError?: (error: unknown) => void;
}

/**
 * Notice another device's changes while the app is open: on start, when the
 * tab becomes visible, when the browser comes back online, and on an
 * interval while visible. One `stat` per tick when nothing changed. Any
 * store with a `version` is watched; a store the app cannot use silently
 * (Drive without a token) is skipped rather than prompted for.
 */
export function startLedgerWatch(opts: LedgerWatchOptions): { stop(): void; pullNow(): Promise<PullResult | null> } {
  const db = opts.db ?? defaultDb;
  const deps = opts.deps ?? defaultLedgerDeps;
  const interval = opts.intervalMs ?? LEDGER_WATCH_INTERVAL_MS;
  let stopped = false;
  let running: Promise<PullResult | null> | null = null;

  const pullNow = (): Promise<PullResult | null> => {
    if (stopped) return Promise.resolve(null);
    if (running) return running;
    running = (async () => {
      try {
        const companyId = await opts.companyId();
        if (!companyId || !deps.online()) return null;
        const store = deps.store();
        if (!store?.capabilities.versioning || !(await store.isAuthorized())) return null;
        return await pullLedgerNow(companyId, db, deps);
      } catch (e) {
        opts.onError?.(e);
        return null;
      } finally {
        running = null;
      }
    })();
    return running;
  };

  const hasDocument = typeof document !== 'undefined';
  const hasWindow = typeof window !== 'undefined';
  const onVisible = () => {
    if (hasDocument && document.visibilityState === 'visible') void pullNow();
  };
  const onTick = () => {
    if (!hasDocument || document.visibilityState === 'visible') void pullNow();
  };
  const onOnline = () => void pullNow();
  if (hasDocument) document.addEventListener('visibilitychange', onVisible);
  if (hasWindow) window.addEventListener('online', onOnline);
  const timer = setInterval(onTick, interval);
  void pullNow();

  return {
    stop() {
      stopped = true;
      clearInterval(timer);
      if (hasDocument) document.removeEventListener('visibilitychange', onVisible);
      if (hasWindow) window.removeEventListener('online', onOnline);
    },
    pullNow,
  };
}
